if (typeof VariantAvailability !== 'function') {

    class VariantAvailability extends HTMLElement {

        constructor() {

            super();
            this.storeSelector = document.querySelector('store-selector[data-main-selector]');

            this.selectedStore = localStorage.getItem('selected-store');

            this.selectors = {
                storeAvailability: '.store-availability',
                changeLocationButton: '.change-location-button',
                modalStoreSelector: 'modal-store-selector',
                storeAvailabilityContainer: '.variant_availability__option--store-pickup',
                shippingAvailabilityContainer: '.variant_availability__option--shipping',
                localDeliveryAvailabilityContainer: '.variant_availability__option--local-delivery',
            }

            this.elements = {
                storeAvailability: this.querySelector(this.selectors.storeAvailability),
                changeLocationButton: this.querySelector(this.selectors.changeLocationButton),
                modalStoreSelector: this.querySelector(this.selectors.modalStoreSelector),
                storeAvailabilityContainer: this.querySelector(this.selectors.storeAvailabilityContainer),
                shippingAvailabilityContainer: this.querySelector(this.selectors.shippingAvailabilityContainer),
                localDeliveryAvailabilityContainer: this.querySelector(this.selectors.localDeliveryAvailabilityContainer),
            }

            this.classes = {
                isHidden: 'is-hidden',
            }

            this.variantsAvailability = JSON.parse(this.dataset.variantData);
            this.variantId = this.dataset.variantId;

        }

        // ===== DATE ESTIMATION HELPERS =====

        /**
         * Get current time in Eastern timezone
         */
        getNowEastern() {
            return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
        }

        /**
         * Format a date as "Wednesday, July 23"
         */
        formatDate(date) {
            return date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
            });
        }

        /**
         * Add business days (Monday–Friday only) to a date
         */
        addBusinessDays(startDate, days) {
            let date = new Date(startDate);
            let added = 0;
            while (added < days) {
                date.setDate(date.getDate() + 1);
                if (date.getDay() !== 0 && date.getDay() !== 6) {
                    added++;
                }
            }
            return date;
        }

        /**
         * Check if the currently selected store is open on Sunday.
         * Matches against partial store slugs in data-sunday-stores (e.g. "bel-air,kingstown").
         */
        isStoreOpenSunday() {
            const sundayStores = (this.dataset.sundayStores || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
            if (!this.selectedStore || sundayStores.length === 0) return false;
            return sundayStores.some(store => this.selectedStore.includes(store));
        }

        /**
         * Get the estimated pickup text based on inventory status.
         *
         * In stock:
         *   - Mon–Sat (all stores) or Sunday (Bel Air / Kingstown): before cutoff → "Ready today"
         *   - After cutoff or store closed Sunday → "Ready by [next open day]"
         * Out of stock:
         *   - "Usually available for pickup in 3–4 business days"
         */
        getEstimatedPickupText(isInStock) {
            if (!isInStock) {
                return 'Usually available for pickup in 3–4 business days';
            }

            const now = this.getNowEastern();
            const hour = now.getHours();
            const day = now.getDay(); // 0 = Sunday, 6 = Saturday
            const cutoffHour = parseInt(this.dataset.pickupCutoffHour) || 15;

            // Determine if the selected store is open today
            const isSunday = day === 0;
            const storeOpenToday = !isSunday || this.isStoreOpenSunday();

            // Before cutoff and store is open → ready within timeframe
            if (storeOpenToday && hour < cutoffHour) {
                return this.dataset.pickupReadyText || 'Ready in about 1 hour';
            }

            // After cutoff or store closed: find next open day
            let nextDate = new Date(now);
            nextDate.setDate(nextDate.getDate() + 1);

            // Skip Sunday if store is not open on Sundays
            while (nextDate.getDay() === 0 && !this.isStoreOpenSunday()) {
                nextDate.setDate(nextDate.getDate() + 1);
            }

            return 'Ready by ' + this.formatDate(nextDate);
        }

        /**
         * Get the estimated delivery text.
         *
         * Pallet-tagged products → "Pallet Delivery"
         * Standard products:
         *   - If before cutoff on a weekday → processing starts today, deliver in N business days
         *   - If after cutoff or weekend → processing starts next business day
         */
        getEstimatedDeliveryText() {
            // Check for pallet tag
            const productTags = (this.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
            if (productTags.includes('pallet')) {
                return 'Pallet Delivery - Delivery Times May Vary';
            }

            const now = this.getNowEastern();
            const hour = now.getHours();
            const day = now.getDay(); // 0 = Sunday, 6 = Saturday
            const cutoffHour = parseInt(this.dataset.pickupCutoffHour) || 15;
            const leadDays = parseInt(this.dataset.deliveryLeadDays) || 2;

            let processingStart = new Date(now);

            // If after cutoff or on a weekend, move to next business day
            if (hour >= cutoffHour || day === 0 || day === 6) {
                processingStart.setDate(processingStart.getDate() + 1);
                while (processingStart.getDay() === 0 || processingStart.getDay() === 6) {
                    processingStart.setDate(processingStart.getDate() + 1);
                }
            }

            // Add lead business days from the processing start
            const deliveryDate = this.addBusinessDays(processingStart, leadDays);

            return 'Delivery as early as ' + this.formatDate(deliveryDate) + ' · may vary by area';
        }

        // ===== DATE DISPLAY UPDATERS =====

        updatePickupDate(isInStock) {
            const dateEl = this.querySelector('.variant_availability__pickup-date');
            if (dateEl) {
                dateEl.textContent = this.getEstimatedPickupText(isInStock);
            }
        }

        updateDeliveryDate() {
            const dateEl = this.querySelector('.variant_availability__delivery-date');
            if (dateEl) {
                dateEl.textContent = this.getEstimatedDeliveryText();
            }
        }

        // ===== EXISTING METHODS =====

        updateDisplayStoreAvailability() {

            this.innerHTMLText = null;
            let isInStock = false;

            if (!this.elements.storeAvailabilityContainer.classList.contains(this.classes.isHidden)) {
                this.elements.storeAvailabilityContainer.classList.add(this.classes.isHidden);
            }

            // Check if no location is selected
            if (!this.selectedStore) {
                this.innerHTMLText = "No location selected";
                this.elements.storeAvailability.innerHTML = `<p>No location selected</p>`;
                if (this.elements.storeAvailabilityContainer.classList.contains(this.classes.isHidden)) {
                    this.elements.storeAvailabilityContainer.classList.remove(this.classes.isHidden);
                }
                this.updatePickupDate(false);
                this.updateDeliveryDate();
                return;
            }

            // Find the matching variant first
            const matchingVariant = this.variantsAvailability.find(variant =>
                variant.variantId.toString() === this.variantId.toString()
            );


            if (matchingVariant) {
                // Handle untracked variants - show as available at all stores
                if (matchingVariant.availability === "untracked") {
                    isInStock = true;
                    this.innerHTMLText = this.wrapStockText(
                        this.dataset.textAvailable.replace('[LOCATION]', this.selectedStore?.replaceAll('-', ' ')),
                        'in stock',
                        'in-stock'
                    );
                } else {
                    // Find the matching location
                    const matchingLocation = matchingVariant.availability.find(availability => {
                        const updatedLocation = availability.location.replaceAll(' ', '-').toLowerCase();
                        return updatedLocation === this.selectedStore;
                    });

                    if (matchingLocation) {
                        // Found matching location
                        const availableQty = Number(matchingLocation.available);
                        const limitedThreshold = Number(this.dataset.limitedThreshold) || 3;

                        if (availableQty > 0) {
                            isInStock = true;

                            // Check if it's limited availability (at or below threshold)
                            if (availableQty <= limitedThreshold) {

                                this.innerHTMLText = this.wrapStockText(
                                    this.dataset.textLimited.replace('[LOCATION]', matchingLocation.location),
                                    'Limited Availability',
                                    'limited-stock'
                                );
                            } else {

                                this.innerHTMLText = this.wrapStockText(
                                    this.dataset.textAvailable.replace('[LOCATION]', matchingLocation.location),
                                    'in stock',
                                    'in-stock'
                                );
                            }
                        } else {
                            isInStock = false;

                            this.innerHTMLText = this.wrapStockText(
                                this.dataset.textUnavailable.replace('[LOCATION]', matchingLocation.location),
                                'out of stock',
                                'out-of-stock'
                            );
                        }
                    }
                }

                if (this.elements.storeAvailabilityContainer.classList.contains(this.classes.isHidden)) {
                    this.elements.storeAvailabilityContainer.classList.remove(this.classes.isHidden);
                }
            }

            if (this.elements.storeAvailabilityContainer.classList.contains(this.classes.isHidden)) {
                this.elements.storeAvailabilityContainer.classList.remove(this.classes.isHidden);
            }

            this.elements.storeAvailability.innerHTML = `<p>${this.innerHTMLText || ''}</p>`;

            // Update estimated dates
            this.updatePickupDate(isInStock);
            this.updateDeliveryDate();
        }

        updateBasedOnVariantWeight(variantId) {

            const matchingVariant = this.variantsAvailability.find(variant =>
                variant.variantId.toString() === variantId.toString()
            );

            const weight = Number(matchingVariant.weight);
            const shippingWeight = Number(this.dataset.shippingWeight);
            const localDeliveryWeight = Number(this.dataset.localDeliveryWeight);


            // Handle shipping weight
            if (shippingWeight > 0) {
                if (weight > shippingWeight) {
                    this.elements.shippingAvailabilityContainer.classList.add(this.classes.isHidden);
                } else {
                    this.elements.shippingAvailabilityContainer.classList.remove(this.classes.isHidden);
                }
            }

            // Handle local delivery weight
            if (localDeliveryWeight > 0) {
                if (weight > localDeliveryWeight) {
                    this.elements.localDeliveryAvailabilityContainer.classList.add(this.classes.isHidden);
                } else {
                    this.elements.localDeliveryAvailabilityContainer.classList.remove(this.classes.isHidden);
                }
            }

        }

        wrapStockText(text, searchTerm, className) {
            // Case-insensitive replacement that preserves original case
            const regex = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            return text.replace(regex, (match) => `<span class="${className}">${match}</span>`);
        }


        connectedCallback() {
            this.updateDisplayStoreAvailability();

            this.storeSelector.addEventListener('storechanged',
                () => {
                    this.selectedStore = localStorage.getItem('selected-store');
                    this.updateDisplayStoreAvailability();
                }
            );

            document.addEventListener('VARIANT_CHANGE', (e) => {
                this.variantId = e.target.currentVariant.id;
                this.updateDisplayStoreAvailability();
                this.updateBasedOnVariantWeight(this.variantId);
            });

            this.elements.changeLocationButton.addEventListener('click', () => {
                document.getElementById(this.selectors.modalStoreSelector)?.show();
            });

        }

    }

    if (typeof customElements.get('variant-availability') == 'undefined') {
        customElements.define('variant-availability', VariantAvailability);
    }
}